import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';

const MerchantsPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  useEffect(() => { fetchMerchants(); }, []);

  const fetchMerchants = async () => {
    setLoading(true); setError('');
    try {
      const response = await apiService?.listBusinesses();
      const data = response?.data;
      const list = data?.details || data?.data || data?.businesses || data?.items || data;
      setMerchants(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const isActive = status === 1 || status === '1' || status === true;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? 'bg-success/10 text-success' : 'bg-muted text-text-secondary'
      }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">Merchant List</h1>
              <p className="text-text-secondary mt-1">
                All approved businesses
                {merchants?.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {merchants?.length} merchant{merchants?.length !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
            </div>
            <button onClick={fetchMerchants} className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted">
              <Icon name="RefreshCw" size={16} className="text-text-secondary" />
              <span>Refresh</span>
            </button>
          </div>

          {error && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {['Business Name', 'Email', 'Phone', 'Country', 'MCC', 'Status', 'Actions']?.map(col => (
                      <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide whitespace-nowrap">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="text-center py-12">
                      <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
                    </td></tr>
                  ) : merchants?.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center space-y-2">
                        <Icon name="Store" size={32} className="text-text-secondary" />
                        <p className="text-text-secondary text-sm">No merchants found</p>
                      </div>
                    </td></tr>
                  ) : (
                    merchants?.map((m, i) => (
                      <tr key={m?.merchant_id || i} className="border-b border-border last:border-0 hover:bg-muted/30 transition-smooth">
                        <td className="px-4 py-3 text-sm font-medium text-text-primary">{m?.business_name || m?.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary">{m?.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary">{m?.phone || m?.phone_number || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary">{m?.country || '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary">{m?.mcc || '-'}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={m?.status} />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setSelectedMerchant(m)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-primary text-white rounded-lg text-xs hover:bg-primary/90 transition-colors"
                          >
                            <Icon name="Eye" size={12} />
                            <span>View</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
      {/* Merchant Detail Modal */}
      {selectedMerchant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedMerchant(null)} />
          <div className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Building2" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-text-primary">
                    {selectedMerchant?.business_name || 'Business Details'}
                  </h2>
                  {selectedMerchant?.business_code && (
                    <p className="text-xs font-mono text-text-secondary">{selectedMerchant?.business_code}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedMerchant(null)}
                className="p-1.5 rounded-lg hover:bg-muted text-text-secondary"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 overflow-y-auto flex-1">
              {/* Business Info */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Email</span>
                  <span className="text-sm text-text-primary bg-muted px-3 py-2 rounded-lg truncate">
                    {selectedMerchant?.email || '-'}
                  </span>
                </div>
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Country</span>
                  <span className="text-sm text-text-primary bg-muted px-3 py-2 rounded-lg">
                    {selectedMerchant?.country || '-'}
                  </span>
                </div>
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">MCC</span>
                  <span className="text-sm text-text-primary bg-muted px-3 py-2 rounded-lg">
                    {selectedMerchant?.mcc || '-'}
                  </span>
                </div>
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Status</span>
                  <div className="px-3 py-2">
                    <StatusBadge status={selectedMerchant?.status} />
                  </div>
                </div>
              </div>

              {/* Merchants List */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Store" size={15} className="text-text-secondary" />
                  <span className="text-sm font-semibold text-text-primary">
                    Attached Merchants
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {selectedMerchant?.merchants?.length || 0}
                  </span>
                </div>

                {!selectedMerchant?.merchants?.length ? (
                  <div className="flex flex-col items-center justify-center py-8 space-y-2 border border-dashed border-border rounded-lg">
                    <Icon name="Store" size={24} className="text-text-secondary" />
                    <p className="text-sm text-text-secondary">No merchants attached to this business</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedMerchant?.merchants?.map((merchant, idx) => (
                      <div
                        key={merchant?.merchant_id || idx}
                        className="flex items-center justify-between px-4 py-3 border border-border rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className="w-7 h-7 bg-accent/10 rounded-md flex items-center justify-center flex-shrink-0">
                            <Icon name="Store" size={13} className="text-accent" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-mono font-medium text-text-primary truncate">
                              {merchant?.merchant_id || '-'}
                            </p>
                            {merchant?.mcc && (
                              <p className="text-xs text-text-secondary">MCC: {merchant?.mcc}</p>
                            )}
                          </div>
                        </div>
                        <StatusBadge status={merchant?.status} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-5 py-4 border-t border-border flex-shrink-0">
              <button
                onClick={() => setSelectedMerchant(null)}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-primary hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantsPage;
